/* match renderer — pitch, goal, squad, orb, replay zoom, HUD. Sprite blits via the atlas helper */
import { CPU_TIERS, DEFENDER_X, floorGlow, GOAL_X, H, MARKERS, MATES, MAX_Y, MIN_Y, PLAYER_X, REF_GROUND_Y, REF_HEIGHT, SPRITES, TEAMS, W } from "./constants";

/* "#ff2d46" -> "255, 46, 70" for rgba() gradients */
const hexRgb = (h) => {
  const n = parseInt(h.slice(1), 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

/* goal explosion — ticks + draws a neon spark burst in the given "r, g, b" */
const drawSparks = (ctx, parts, rgb) => {
  if (!parts || !parts.length) return;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  parts.forEach((p) => {
    if (p.life <= 0) return;
    p.x += p.vx;
    p.y += p.vy;
    p.vy += 0.12;
    p.vx *= 0.985;
    p.life -= 1;
    const a = Math.max(0, p.life / p.maxLife);
    ctx.strokeStyle = `rgba(${rgb}, ${a.toFixed(3)})`;
    ctx.lineWidth = p.size;
    ctx.lineCap = "round";
    ctx.shadowColor = `rgb(${rgb})`;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(p.x - p.vx * 1.6, p.y - p.vy * 1.6);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  });
  ctx.restore();
};

function drawSprite(ctx, sheet, rect, centerX, groundY, height, flip) {  const [sx, sy, sw, sh] = rect;
  const width = (height * sw) / sh;
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  if (flip) {
    ctx.translate(centerX, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(sheet, sx, sy, sw, sh, -width / 2, groundY - height, width, height);
  } else {
    ctx.drawImage(sheet, sx, sy, sw, sh, centerX - width / 2, groundY - height, width, height);
  }
  ctx.restore();
}

/* one draw path for every franchise: red/white blit from the classic atlas,
   expansion teams (vultures, silverbacks) draw from preloaded transparent PNGs */
function drawTeam(ctx, { sheetRef, teamSpritesRef }, teamKey, pose, centerX, groundY, height, flip) {
  const rect = SPRITES[teamKey]?.[pose];
  if (rect && sheetRef.current) {
    drawSprite(ctx, sheetRef.current, rect, centerX, groundY, height, flip);
    return true;
  }
  const img = teamSpritesRef?.current?.[teamKey]?.[pose];
  if (img && img.complete && img.naturalWidth > 0) {
    const width = (height * img.naturalWidth) / img.naturalHeight;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    if (flip) {
      ctx.translate(centerX, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -width / 2, groundY - height, width, height);
    } else {
      ctx.drawImage(img, centerX - width / 2, groundY - height, width, height);
    }
    ctx.restore();
    return true;
  }
  return false;
}

export function renderMatch(g, ctx, { sheetRef, phaseRef, teamKey, rival, refSpritesRef, teamSpritesRef, arenaRef, floorRef }) {
      const spriteEnv = { sheetRef, teamSpritesRef };
      const goalY = 210 + Math.sin(g.goalPhase) * 116;
      // goal replay: slow zoom into the net while the world is frozen
      const replaying = g.celebrateTimer > 0 && phaseRef.current === "running";
      const zoom = replaying ? 1 + ((90 - g.celebrateTimer) / 90) * 0.3 : 1;
      ctx.save();
      if (replaying) {
        ctx.translate(GOAL_X, goalY);
        ctx.scale(zoom, zoom);
        ctx.translate(-GOAL_X, -goalY);
      }
      // pitch — the picked franchise's home arena (fence + crowd art) under a
      // darkened glass court; plain black glass until the art finishes loading
      const arenaImg = arenaRef?.current?.[teamKey];
      const arenaReady = arenaImg && arenaImg.complete && arenaImg.naturalWidth > 0;
      if (arenaReady) {
        if (g.crowdSurge > 0) {
          // crowd surge — the stands press forward with the celebration
          const env = Math.sin(Math.PI * Math.min(1, 1 - g.crowdSurge / 80));
          const s = 1 + 0.05 * env;
          ctx.save();
          ctx.translate(W / 2, H / 2);
          ctx.scale(s, s);
          ctx.drawImage(arenaImg, -W / 2, -H / 2, W, H);
          ctx.restore();
        } else {
          ctx.drawImage(arenaImg, 0, 0, W, H);
        }
        ctx.fillStyle = "rgba(2, 2, 6, 0.28)";
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.fillStyle = "#020202";
        ctx.fillRect(0, 0, W, H);
      }
      // owner's neon floor art painted inside the court lines (crowd art stays
      // at the margins); a light dim keeps sprites readable over the paint
      const floorImg = floorRef?.current?.[teamKey];
      const floorReady = floorImg && floorImg.complete && floorImg.naturalWidth > 0;
      // glow sync — goal ring + zip streaks borrow the floor's neon color
      const [gr, gg, gb] = floorGlow(teamKey);
      const gl = `${gr}, ${gg}, ${gb}`;
      const glLite = `${Math.min(255, gr + 60)}, ${Math.min(255, gg + 60)}, ${Math.min(255, gb + 60)}`;
      if (floorReady) {
        ctx.drawImage(floorImg, 24, MIN_Y - 34, W - 48, MAX_Y - MIN_Y + 84);
        // dark tint so players stand out on the brighter floors
        ctx.fillStyle = "rgba(2, 2, 6, 0.4)";
        ctx.fillRect(24, MIN_Y - 34, W - 48, MAX_Y - MIN_Y + 84);
      } else if (arenaReady) {
        ctx.fillStyle = "rgba(2, 2, 6, 0.62)";
        ctx.fillRect(24, MIN_Y - 34, W - 48, MAX_Y - MIN_Y + 84);
      }
      // thin white floor stripes (only on the bare court — not over the floor art)
      if (!floorReady) {
        ctx.strokeStyle = "rgba(255, 255, 255, 0.07)";
        ctx.lineWidth = 1;
        for (let y = MIN_Y - 30; y <= MAX_Y + 46; y += 22) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(W, y);
          ctx.stroke();
        }
      }
      // crowd wave — after a goal the stands light in the floor color and the
      // pennants go up along both fences for the celebration
      if (arenaReady && g.crowdSurge > 0) {
        const env = Math.sin(Math.PI * Math.min(1, 1 - g.crowdSurge / 80));
        ctx.save();
        ctx.fillStyle = `rgba(${gl}, ${(0.16 * env).toFixed(3)})`;
        ctx.fillRect(0, 0, W, MIN_Y - 34);
        ctx.fillRect(0, MAX_Y + 50, W, H - (MAX_Y + 50));
        const wave = g.frame * 0.22;
        for (let row = 0; row < 2; row += 1) {
          const baseY = row === 0 ? MIN_Y - 42 : MAX_Y + 58;
          for (let i = 0; i < 12; i += 1) {
            const x = 36 + i * ((W - 72) / 11);
            const sway = Math.sin(wave + i * 0.9 + row * 2.1) * 5 * env;
            const lift = Math.abs(Math.cos(wave * 0.7 + i)) * 6 * env;
            ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x, baseY + 10);
            ctx.lineTo(x + sway, baseY - 8 - lift);
            ctx.stroke();
            ctx.fillStyle = i % 2 ? `rgba(${gl}, ${(0.55 + 0.4 * env).toFixed(3)})` : "rgba(255, 255, 255, 0.8)";
            ctx.beginPath();
            ctx.moveTo(x + sway, baseY - 8 - lift);
            ctx.lineTo(x + sway + 9 + env * 3, baseY - 5 - lift);
            ctx.lineTo(x + sway, baseY - 2 - lift);
            ctx.closePath();
            ctx.fill();
          }
        }
        ctx.restore();
      }
      // court border — thin white
      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      ctx.strokeRect(24, MIN_Y - 34, W - 48, MAX_Y - MIN_Y + 84);
      // halfway line, circle and spot — thin red
      ctx.strokeStyle = "rgba(255, 0, 51, 0.75)";
      ctx.beginPath();
      ctx.moveTo(W / 2, MIN_Y - 34);
      ctx.lineTo(W / 2, MAX_Y + 50);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(W / 2, 214, 52, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(W / 2, 214, 4, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255, 0, 51, 0.75)";
      ctx.fill();
      // AL RIOTBALL wordmark painted on the floor — only on the bare court;
      // the arena backdrops and floor art already carry the painted stencil
      if (!arenaReady && !floorReady) {
        ctx.save();
        ctx.textAlign = "center";
        ctx.font = "76px 'Bebas Neue', sans-serif";
        ctx.lineWidth = 2;
        ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
        ctx.strokeText("AL RIOTBALL", W / 2, 242);
        ctx.fillStyle = "rgba(255, 0, 51, 0.08)";
        ctx.fillText("AL RIOTBALL", W / 2, 242);
        ctx.restore();
      }

      // electronic goal — rotating ring with energy core, synced to the floor's neon
      ctx.save();
      ctx.translate(GOAL_X, goalY);
      const glow = 0.65 + Math.sin(g.frame * 0.15) * 0.25;
      ctx.strokeStyle = `rgba(${gl}, ${glow})`;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(0, 0, 34, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = `rgba(${glLite}, 0.8)`;
      ctx.lineWidth = 2;
      for (let i = 0; i < 3; i += 1) {
        const rot = g.frame * 0.05 + (i * Math.PI * 2) / 3;
        ctx.beginPath();
        ctx.arc(0, 0, 27, rot, rot + 1.4);
        ctx.stroke();
      }
      const core = ctx.createRadialGradient(0, 0, 2, 0, 0, 24);
      core.addColorStop(0, `rgba(${gl}, 0.75)`);
      core.addColorStop(1, `rgba(${gl}, 0)`);
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(0, 0, 24, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // VS MODE — your own goal on the left, glowing in the rival's colors
      if (g.mode === "vs") {
        ctx.save();
        ctx.translate(56, g.cpuGoalY);
        const rGlow = 0.6 + Math.sin(g.frame * 0.15 + 2) * 0.25;
        ctx.strokeStyle = TEAMS[rival].color;
        ctx.globalAlpha = rGlow;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.arc(0, 0, 34, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 0.8;
        ctx.lineWidth = 2;
        for (let i = 0; i < 3; i += 1) {
          const rot = -g.frame * 0.05 + (i * Math.PI * 2) / 3;
          ctx.beginPath();
          ctx.arc(0, 0, 27, rot, rot + 1.4);
          ctx.stroke();
        }
        ctx.restore();
      }

      // VS MODE — the net takes the ball: ripple rings burst when the CPU scores
      if (g.mode === "vs" && g.cpuNetRipple > 0) {
        const rt = 1 - g.cpuNetRipple / 34;
        for (let i = 0; i < 3; i += 1) {
          ctx.save();
          ctx.translate(56, g.cpuGoalY);
          ctx.globalAlpha = (1 - rt) * (0.8 - i * 0.22);
          ctx.strokeStyle = i === 0 ? "#ffffff" : TEAMS[rival].color;
          ctx.lineWidth = Math.max(1, 3 - i);
          ctx.beginPath();
          ctx.arc(0, 0, 18 + rt * 44 + i * 12, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
        // the ball visibly sitting in the net for the freeze-frame
        ctx.save();
        ctx.shadowColor = TEAMS[rival].color;
        ctx.shadowBlur = 20;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(52, g.cpuGoalY, Math.max(4, 11 - rt * 6), 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // NIGHT SHIFT — the floodlights cut; the pitch falls dark and the Ravens own it
      if (g.lightsOut) {
        ctx.fillStyle = "rgba(2, 3, 16, 0.55)";
        ctx.fillRect(24, 56, W - 48, 334);
        ctx.fillStyle = "rgba(154, 107, 255, 0.75)";
        ctx.font = "700 10px 'Courier New', monospace";
        ctx.textAlign = "left";
        ctx.fillText("NIGHT SHIFT — LIGHTS OUT", 36, 72);
      }

      // squadmates — 2 mid + 2 front holding formation, slightly smaller than the runner
      const matesDrawn = MATES.map((mate, i) => {
        const bobY = mate.y + Math.sin(g.frame * 0.05 + i * 1.7) * 7;
        const pose = Math.floor(g.frame / 14 + i) % 2 ? "run1" : "idle";
        return drawTeam(ctx, spriteEnv, teamKey, pose, mate.x, bobY, 100, false);
      }).every(Boolean);
      // rival markers man-marking the squad — rival kit shadowing every lane
      const markersDrawn = MARKERS.map((marker, i) =>
        drawTeam(ctx, spriteEnv, rival, "idle", marker.x, g.markY[i], 100, true),
      ).every(Boolean);

      // defender (rival kit, facing the player)
      const defenderPose = g.defenderFallTimer > 0 ? "fall" : g.defenderCatchTimer > 0 ? "catch" : "idle";
      const defenderDrawn = drawTeam(
        ctx, spriteEnv, rival, defenderPose, DEFENDER_X, g.defenderY, g.defenderFallTimer > 0 ? 74 : 118, true,
      );
      // player (chosen kit)
      let playerPose = "idle";
      if (g.celebrateTimer > 0) playerPose = "celebrate";
      else if (g.throwTimer > 0) playerPose = "throw";
      else if (g.playerMoving) playerPose = Math.floor(g.frame / 8) % 2 ? "run1" : "run2";
      const playerDrawn = drawTeam(ctx, spriteEnv, teamKey, playerPose, PLAYER_X, g.playerY, 118, false);

      if (!matesDrawn || !markersDrawn || !defenderDrawn || !playerDrawn) {
        // sprites still loading — flat kit-colored stand-ins so the match stays readable
        if (!playerDrawn) {
          ctx.fillStyle = TEAMS[teamKey].color;
          ctx.fillRect(PLAYER_X - 16, g.playerY - 96, 32, 96);
        }
        if (!defenderDrawn) {
          ctx.fillStyle = TEAMS[rival].color;
          ctx.fillRect(DEFENDER_X - 16, g.defenderY - 96, 32, 96);
        }
      }

      // VS MODE — the CPU carrier drives at your goal; its shot glows rival-hot
      if (g.mode === "vs" && g.possession === "cpu" && g.cpu) {
        const c = g.cpu;
        const cpuPose = c.phase === "drive" ? (Math.floor(g.frame / 8) % 2 ? "run1" : "run2") : c.phase === "windup" || c.phase === "combo" ? "throw" : "idle";
        drawTeam(ctx, spriteEnv, rival, cpuPose, c.x, c.y, 118, true);
        // the orb rides with the carrier until the release (or a combo zip)
        if (c.phase !== "ball" && !(c.phase === "combo" && g.cpuBall)) {
          ctx.save();
          ctx.shadowColor = TEAMS[rival].color;
          ctx.shadowBlur = 18;
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(c.x - 34, c.y - 62, 10, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          // steal telegraph — the ring burns white when your lane read is armed
          const TT = CPU_TIERS[g.tier] || CPU_TIERS.easy;
          const readLane = Math.abs(g.playerY - c.y) < TT.stealRange;
          const stealable = c.phase === "windup" || (c.phase === "drive" && c.x <= TT.stealZoneX);
          if (stealable) {
            const pulse = 17 + Math.sin(g.frame * 0.25) * 3;
            ctx.save();
            ctx.strokeStyle = readLane ? "#ffffff" : "rgba(255, 176, 32, 0.65)";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(c.x - 34, c.y - 62, pulse, 0, Math.PI * 2);
            ctx.stroke();
            if (readLane) {
              ctx.fillStyle = "#ffffff";
              ctx.font = "800 9px " + "'JetBrains Mono', monospace";
              ctx.textAlign = "center";
              ctx.fillText("STEAL", c.x - 34, c.y - 84);
            }
            ctx.restore();
          }
        }
      }
      if (g.mode === "vs" && g.cpuBall) {
        const b = g.cpuBall;
        ctx.save();
        const rc = hexRgb(TEAMS[rival].color);
        // telegraph the long switch — a dashed lane you can step into for the cut
        const ca = g.cpu;
        if (ca?.phase === "combo" && ca.comboLong && ca.comboTo) {
          ctx.strokeStyle = `rgba(${rc}, 0.3)`;
          ctx.setLineDash([6, 8]);
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(b.x, b.y);
          ctx.lineTo(ca.comboTo.x, ca.comboTo.y);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        // rival-hot trail behind their shots and combo zips
        if (b.vx || b.vy) {
          const tx = b.x - b.vx * 2.2;
          const ty = b.y - b.vy * 2.2;
          const trail = ctx.createLinearGradient(tx, ty, b.x, b.y);
          trail.addColorStop(0, `rgba(${rc}, 0)`);
          trail.addColorStop(1, `rgba(${rc}, 0.9)`);
          ctx.strokeStyle = trail;
          ctx.lineWidth = 6;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(tx, ty);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
        ctx.shadowColor = TEAMS[rival].color;
        ctx.shadowBlur = 22;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(b.x, b.y, 11, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.strokeStyle = TEAMS[rival].color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(b.x, b.y, 14, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // the referee — near-touchline official in the foreground, drawn BEFORE
      // the orb so the glowing ball never hides behind him
      const refSprites = refSpritesRef?.current;
      if (refSprites && refSprites.idle.complete && refSprites.idle.naturalWidth > 0) {
        const r = g.ref;
        let img = refSprites.idle;
        if (r.state === "foul") img = refSprites.foul;
        else if (r.state === "shield") img = refSprites.shield;
        else if (r.state === "walk") img = refSprites.run[Math.floor(g.frame / 7) % refSprites.run.length];
        if (img.complete && img.naturalWidth > 0) {
          const rh = REF_HEIGHT;
          const rw = (rh * img.naturalWidth) / img.naturalHeight;
          ctx.save();
          ctx.imageSmoothingEnabled = false;
          // contact shadow on the touchline
          ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
          ctx.beginPath();
          ctx.ellipse(r.x, REF_GROUND_Y + 3, rw * 0.4, 4.5, 0, 0, Math.PI * 2);
          ctx.fill();
          if (r.state === "walk" && r.dir < 0) {
            ctx.translate(r.x, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(img, -rw / 2, REF_GROUND_Y - rh, rw, rh);
          } else {
            ctx.drawImage(img, r.x - rw / 2, REF_GROUND_Y - rh, rw, rh);
          }
          ctx.restore();
        }
      }

      // the orb
      if (g.orb.state === "held" && g.possession === "player") {
        g.orb.x = PLAYER_X + 46;
        g.orb.y = g.playerY - 66;
      }
      if (g.celebrateTimer <= 0) {
        ctx.save();
        // zip-pass streak — a neon trail behind the flying orb, synced to the floor
        if (g.orb.state === "flying") {
          const tx = g.orb.x - g.orb.vx * 2.4;
          const ty = g.orb.y - g.orb.vy * 2.4;
          const trail = ctx.createLinearGradient(tx, ty, g.orb.x, g.orb.y);
          trail.addColorStop(0, `rgba(${gl}, 0)`);
          trail.addColorStop(1, `rgba(${gl}, 0.9)`);
          ctx.strokeStyle = trail;
          ctx.lineWidth = 7;
          ctx.lineCap = "round";
          ctx.shadowColor = `rgb(${gl})`;
          ctx.shadowBlur = 14;
          ctx.beginPath();
          ctx.moveTo(tx, ty);
          ctx.lineTo(g.orb.x, g.orb.y);
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
        ctx.shadowColor = "#ffdf6b";
        ctx.shadowBlur = 16;
        ctx.fillStyle = "#ffdf6b";
        ctx.beginPath();
        ctx.arc(g.orb.x, g.orb.y, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#8a5b00";
        ctx.beginPath();
        ctx.arc(g.orb.x, g.orb.y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // goal explosions — neon sparks in the floor's glow (rival color for CPU goals)
      drawSparks(ctx, g.goalSparks, gl);
      if (g.mode === "vs") drawSparks(ctx, g.cpuGoalSparks, hexRgb(TEAMS[rival].color));

      ctx.restore();

      // instant-replay white flash
      if (g.replayFlash > 0) {
        ctx.fillStyle = `rgba(255, 255, 255, ${(0.85 * (g.replayFlash / 14)).toFixed(3)})`;
        ctx.fillRect(0, 0, W, H);
      }

      // final-stretch vignette pulse
      if (phaseRef.current === "running" && g.timeLeft <= 600) {
        const pulse = 0.18 + 0.14 * Math.sin(g.frame * 0.3);
        const vignette = ctx.createRadialGradient(W / 2, H / 2, H * 0.28, W / 2, H / 2, H * 0.85);
        vignette.addColorStop(0, "rgba(255, 0, 51, 0)");
        vignette.addColorStop(1, `rgba(255, 0, 51, ${pulse.toFixed(3)})`);
        ctx.fillStyle = vignette;
        ctx.fillRect(0, 0, W, H);
      }

      // score, timer, trait, heat meter and banner flashes live in the DOM HUD
      // above the canvas now — the court stays clear for the players
}
